import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { Children, ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import { SectionLabel } from "./SectionLabel";

/** The width of a dialog that asks for a line or two, in px. */
const DEFAULT_WIDTH = 444;

/**
 * The frame the dialogs share: a title with what the dialog asks for under it,
 * the content, and the buttons on a quiet strip at its foot.
 */
export const DialogShell = ({
  open,
  onClose,
  title,
  description,
  actions,
  children,
  width = DEFAULT_WIDTH,
  header,
  sectioned = false,
  closeButton = true,
}: {
  open: boolean;
  /** Called for the close button, a click beside the dialog and Escape alike. */
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** The buttons at the foot, the one that confirms last. */
  actions: ReactNode;
  children?: ReactNode;
  /**
   * How wide the dialog is where the window allows, in px. The names of the
   * theme's breakpoints do not serve for it: the theme's "sm" is narrower than
   * what "xs" stands for with a dialog.
   */
  width?: number;
  /**
   * Stands in place of the title and the description, for a dialog that opens
   * with figures. The title then only names the dialog.
   */
  header?: ReactNode;
  /** The content is a row of {@link DialogSection}s, which reach from edge to edge. */
  sectioned?: boolean;
  /** A dialog that only asks to confirm closes by its buttons. */
  closeButton?: boolean;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "dialog" });
  const titleId = useId();
  const descriptionId = useId();
  // conditions that hold for nothing leave children that draw nothing
  const hasContent = Children.toArray(children).length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={header ? undefined : titleId}
      aria-label={header && typeof title === "string" ? title : undefined}
      aria-describedby={!header && description ? descriptionId : undefined}
      // as wide as its limit where the window allows and narrower in a small
      // window, rather than as wide as its content happens to be
      fullWidth
      maxWidth={false}
      slotProps={{ paper: { sx: { borderRadius: 3, maxWidth: width } } }}
    >
      {header ?? (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            px: 2.5,
            pt: 2.25,
            pb: hasContent ? 0 : 2.25,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id={titleId}
              component="h2"
              variant="subtitle1"
              sx={{ fontWeight: 700, lineHeight: 1.3 }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                id={descriptionId}
                variant="body2"
                sx={{ mt: 0.5, color: "text.secondary" }}
              >
                {description}
              </Typography>
            )}
          </Box>
          {closeButton && (
            <IconButton
              aria-label={t("close")}
              onClick={onClose}
              size="small"
              edge="end"
              sx={{ flex: "none", mt: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
      {hasContent && (
        <DialogContent
          sx={
            sectioned
              ? { p: 0 }
              : {
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.75,
                  px: 2.5,
                  pt: 2,
                  pb: 2.25,
                  // The content scrolls when it is taller than the dialog may
                  // be, so nothing in it gives way: a part that clips itself
                  // (a card with rounded corners) would otherwise be squeezed
                  // to fit, and the content never grow tall enough to scroll.
                  "& > *": { flexShrink: 0 },
                }
          }
        >
          {children}
        </DialogContent>
      )}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.default",
          "& .MuiButton-root": { textTransform: "none", fontWeight: 600 },
        }}
      >
        {actions}
      </DialogActions>
    </Dialog>
  );
};

/**
 * One of the things a dialog asks for when it asks for several: a label, what
 * is wanted, and the controls. A line parts it from the section before it.
 */
export const DialogSection = ({
  label,
  description,
  first = false,
  children,
}: {
  label: ReactNode;
  description?: ReactNode;
  /** The first section follows the title of the dialog without a line. */
  first?: boolean;
  children?: ReactNode;
}) => (
  <Box
    component="section"
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      px: 2.5,
      py: 2,
      ...(first ? { pt: 1.5 } : { borderTop: 1, borderColor: "divider" }),
    }}
  >
    <Box>
      <SectionLabel component="h3">{label}</SectionLabel>
      {description && (
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {description}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);
